// client/src/pages/ShopPOS/components/DenominationModal.tsx
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";

interface DenominationModalProps {
  show: boolean;
  userName: string;
  denominations: Record<string, number>;
  tempClosingNotes: string;
  showAuthorizationField: boolean;
  authorizationCode: string;
  isClosing: boolean;
  isAuthorizing: boolean;
  pendingAuthShift: boolean;
  getCurrencySymbol: () => string;
  calculateTotal: () => number;
  onDenominationChange: (denominations: Record<string, number>) => void;
  onNotesChange: (notes: string) => void;
  onAuthorizationCodeChange: (code: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

export function DenominationModal({
  show,
  userName,
  denominations,
  tempClosingNotes,
  showAuthorizationField,
  authorizationCode,
  isClosing,
  isAuthorizing,
  pendingAuthShift,
  getCurrencySymbol,
  calculateTotal,
  onDenominationChange,
  onNotesChange,
  onAuthorizationCodeChange,
  onConfirm,
  onCancel,
}: DenominationModalProps) {
  if (!show) return null;

  const handleDenominationChange = (key: string, value: string) => {
    onDenominationChange({
      ...denominations,
      [key]: key === "coins" ? parseFloat(value) || 0 : parseInt(value) || 0,
    });
  };

  // {NUEVO: Manejar foco para borrar el 0}
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value === "0") {
      e.target.value = "";
    }
  };

  // {NUEVO: Manejar blur para restaurar 0 si está vacío}
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>, key: string) => {
    if (e.target.value === "") {
      handleDenominationChange(key, "0");
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[85] p-4"
    >
      <div
        className="bg-background rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()} // Evitar que clicks en el contenido cierren el modal
      >
        <h3 className="text-lg font-bold mb-2 text-center">
          Desglose de Efectivo
        </h3>
        <p className="text-sm text-muted-foreground text-center mb-4">
          Vendedor: <span className="font-semibold">{userName}</span>
        </p>

        <div className="space-y-3 max-h-[50vh] overflow-auto">
          {[
            "5000",
            "2000",
            "1000",
            "500",
            "200",
            "100",
            "50",
            "20",
            "10",
            "5",
            "3",
            "1",
          ].map((denom) => (
            <div key={denom} className="flex items-center justify-between">
              <span className="font-medium">Billetes de ${denom}</span>
              <Input
                type="number"
                min="0"
                step="1"
                value={denominations[denom] || 0}
                onFocus={handleFocus}
                onBlur={(e) => handleBlur(e, denom)}
                onChange={(e) =>
                  handleDenominationChange(denom, e.target.value)
                }
                className="w-24 text-right"
                disabled={showAuthorizationField || pendingAuthShift}
                readOnly={showAuthorizationField || pendingAuthShift}
              />
            </div>
          ))}
          <div className="flex items-center justify-between">
            <span className="font-medium">Monedas (total en $)</span>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={denominations.coins || 0}
              onFocus={handleFocus}
              onBlur={(e) => handleBlur(e, "coins")}
              onChange={(e) =>
                handleDenominationChange("coins", e.target.value)
              }
              className="w-24 text-right"
              disabled={showAuthorizationField || pendingAuthShift}
              readOnly={showAuthorizationField || pendingAuthShift}
            />
          </div>

          <div className="border-t pt-3 mt-2">
            <div className="flex justify-between font-bold">
              <span>Total calculado:</span>
              <span>
                {getCurrencySymbol()}
                {calculateTotal().toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          <div>
            <label className="text-sm font-medium mb-1 block">
              Notas u observaciones
            </label>
            <textarea
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Ej: Gastos de transporte, recibos pagados, etc."
              value={tempClosingNotes}
              onChange={(e) => onNotesChange(e.target.value)}
              rows={3}
            />
          </div>

          {showAuthorizationField && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <label className="text-sm font-medium mb-1 block text-amber-800">
                Código de autorización de supervisor *
              </label>
              <p className="text-xs text-amber-700 mb-2">
                Se ha detectado una discrepancia en el arqueo.
              </p>
              <Input
                type="text"
                placeholder="Ingresa el código de 6 dígitos"
                value={authorizationCode}
                onChange={(e) => onAuthorizationCodeChange(e.target.value)}
                className="bg-white"
                maxLength={6}
                autoFocus
              />
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button
              className="flex-1"
              onClick={onConfirm}
              disabled={isClosing || isAuthorizing}
            >
              {isClosing || isAuthorizing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {pendingAuthShift ? "Autorizando..." : "Procesando..."}
                </>
              ) : pendingAuthShift ? (
                "Autorizar con Código"
              ) : (
                "Confirmar Cierre"
              )}
            </Button>
            <Button variant="outline" className="flex-1" onClick={onCancel}>
              Cancelar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
